import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import api from "@/api";
import { useI18n } from "@/i18n";

// READ-ONLY, no action button: a guard that never refuses anything is either useless or
// broken. Two distinct signals — never evaluated (dead) vs evaluated but never refusing
// (inert). The three guards that failed the week of 20/09 were all in the second state.
const TONE = {
  dead: "text-[#FF4B4B]",
  abnormal: "text-[#FF8A3D]",
  inert: "text-[#FFD600]",
  running_in: "text-slate-400",
  ok: "text-[#00FF87]",
};

export default function GuardsPanel() {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/guards").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;

  return (
    <div className="mb-6 rounded-xl p-5 hud-card" data-testid="guards-panel">
      <span className="flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
        <ShieldCheck className={`h-3.5 w-3.5 ${data.counts.dead || data.counts.inert || data.counts.abnormal ? "text-[#FFD600]" : "text-[#00FF87]"}`} />
        {t("admin.guards_title")}
      </span>
      <p className="mt-2 font-mono2 text-[10px] text-slate-600">{t("admin.guards_note")}</p>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[620px] font-mono2 text-[10px]">
          <thead className="text-slate-500">
            <tr className="text-left uppercase tracking-wider">
              <th className="py-1">{t("admin.guard_name")}</th>
              <th className="py-1 pl-4">{t("admin.guard_status")}</th>
              <th className="py-1 pl-4 text-right">{t("admin.guard_evaluated")}</th>
              <th className="py-1 pl-4 text-right">{t("admin.guard_refused")}</th>
              <th className="py-1 pl-4">{t("admin.guard_last_refusal")}</th>
            </tr>
          </thead>
          <tbody className="text-slate-300">
            {data.guards.map((g) => (
              <tr key={g.guard_id} className="border-t border-slate-800/60" data-testid={`guard-row-${g.guard_id}`}>
                <td className="py-1.5">
                  <span className="text-slate-200">{g.guard_id}</span>
                  <span className="ml-2 text-slate-600">{g.where}</span>
                  <div className="text-slate-600">{g.blocks}</div>
                </td>
                <td className={`py-1.5 pl-4 align-top ${TONE[g.status]}`} data-testid={`guard-status-${g.guard_id}`}>
                  {t(`admin.guard_status_${g.status}`)}
                  <div className="text-slate-600">
                    {g.status === "running_in"
                      ? `${t("admin.guard_until")} ${g.running_in_until}`
                      : `${t("admin.guard_since")} ${g.since}`}
                  </div>
                  {g.drift && (
                    <div className="text-[#FF8A3D]" data-testid={`guard-drift-${g.guard_id}`}>
                      {`${(g.drift.rate_7d * 100).toFixed(1)}% ${t("admin.guard_drift_vs")} ${(g.drift.baseline_rate * 100).toFixed(1)}% (x${g.drift.ratio})`}
                    </div>
                  )}
                </td>
                <td className="py-1.5 pl-4 text-right align-top">{g.evaluated_30d}</td>
                <td className={`py-1.5 pl-4 text-right align-top ${g.refused_30d ? "text-[#00FF87]" : "text-slate-500"}`}>
                  {g.refused_30d}
                </td>
                <td className="py-1.5 pl-4 align-top text-slate-400">
                  {g.last_refusal ? (
                    <>
                      {g.last_refusal.at.slice(0, 16).replace("T", " ")}
                      <div className="text-slate-600">{g.last_refusal.reason}</div>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(data.failed_jobs || []).length > 0 && (
        <div className="mt-4" data-testid="guards-failed-jobs">
          {/* Un échec n'est visible que s'il est REMONTÉ : sync_players est mort le 27/09
              sur un KeyError('name') et rien ne l'a signalé pendant une semaine. */}
          <span className="font-mono2 text-[10px] uppercase tracking-wider text-[#FF4D4D]">
            {t("admin.failed_jobs", { h: data.failed_window_h })}
          </span>
          <ul className="mt-1 space-y-1.5 font-mono2 text-[10px] text-slate-300">
            {data.failed_jobs.map((j, i) => (
              <li key={`${j.job}-${j.at}-${i}`} data-testid={`failed-job-${j.job}`}>
                <span className="text-[#FF8A8A]">{j.job}</span>
                <span className="ml-2 text-slate-500">{(j.at || "").slice(0, 16).replace("T", " ")}</span>
                {j.league_id ? <span className="ml-2 text-slate-600">{t("admin.failed_job_league", { id: j.league_id })}</span> : null}
                <span className="ml-2 text-slate-600">{t("admin.failed_job_cost", { r: j.requests, n: j.records })}</span>
                <div className="mt-0.5 whitespace-pre-wrap break-words text-slate-400" data-testid={`failed-job-detail-${j.job}`}>
                  {j.detail || "—"}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(data.idle_jobs || []).length > 0 && (
        <div className="mt-4" data-testid="guards-idle-jobs">
          {/* Un succès à vide répété est le même angle mort qu'une garde qui ne refuse
              jamais : sync_transfers a tourné 4 fois pour 0 requête sans rien signaler. */}
          <span className="font-mono2 text-[10px] uppercase tracking-wider text-[#FFD600]">
            {t("admin.idle_jobs")}
          </span>
          <ul className="mt-1 space-y-1 font-mono2 text-[10px] text-slate-300">
            {data.idle_jobs.map((j) => (
              <li key={j.job} data-testid={`idle-job-${j.job}`}>
                {j.job} — {t("admin.idle_runs", { n: j.idle_runs })}
                <span className="ml-1 text-slate-600">{j.last_detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(data.wasteful_jobs || []).length > 0 && (
        <div className="mt-4" data-testid="guards-wasteful-jobs">
          {/* Payer sans produire : backfill_nationality dépensait 50 requêtes par nuit pour
              remplir 0 champ, et idle_jobs ne le voyait pas (requests:50 est positif). */}
          <span className="font-mono2 text-[10px] uppercase tracking-wider text-[#FF4D4D]">
            {t("admin.wasteful_jobs")}
          </span>
          <ul className="mt-1 space-y-1 font-mono2 text-[10px] text-slate-300">
            {data.wasteful_jobs.map((j) => (
              <li key={j.job} data-testid={`wasteful-job-${j.job}`}>
                {j.job} — {t("admin.wasteful_runs", { n: j.runs, r: j.requests_spent })}
                <span className="ml-1 text-slate-600">{j.last_detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(data.skipped_jobs || []).length > 0 && (
        <div className="mt-4" data-testid="guards-skipped-jobs">
          <span className="font-mono2 text-[10px] uppercase tracking-wider text-[#FFD600]">
            {t("admin.skipped_jobs")}
          </span>
          <ul className="mt-1 space-y-1 font-mono2 text-[10px] text-slate-300">
            {data.skipped_jobs.map((j) => (
              <li key={j.job} data-testid={`skipped-job-${j.job}`}>
                {j.job} — {t("admin.skipped_runs", { n: j.skipped_runs, reason: j.reason })}
                <span className="ml-1 text-slate-600">{j.last_detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
